import { useState, useEffect } from "react";
import { contactsAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const roleColor = r => ({
  "Champion": "#22C55E",
  "Economic Buyer": "#F59E0B",
  "Technical Buyer": ACCENT,
  "Procurement": "#8B5CF6",
  "End User": TEXT_MUTED,
}[r] || TEXT_DIM);

const confColor = v => v >= 80 ? "#22C55E" : v >= 60 ? "#F59E0B" : "#EF4444";

const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);

const ConfBar = ({ value }) => {
  const c = confColor(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 3, background: BORDER, borderRadius: 2 }}>
        <div style={{ width: `${value}%`, height: "100%", background: c, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, color: c, fontFamily: "'DM Mono', monospace", minWidth: 30, textAlign: "right" }}>{value}%</span>
    </div>
  );
};

// Strip internal Exa pipeline call names from evidence labels before
// rendering. Pattern matches `exa_<anything>:` or `call<anything>:` at the
// start of the label. Mirrors stripEvidencePrefix in inference-results.jsx.
const stripEvidencePrefix = (raw) => {
  if (raw == null) return "";
  const s = String(raw).trim();
  return s.replace(/^(exa_[^:\s]*|call[^:\s]*)\s*:\s*/i, "").trim();
};
const _URL_RE = /^https?:\/\/\S+$/i;

const EvidenceChip = ({ label }) => {
  const clean = stripEvidencePrefix(label);
  const isUrl = _URL_RE.test(clean);
  const baseStyle = {
    fontSize: 10, color: ACCENT, background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`,
    borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontFamily: "'DM Mono', monospace",
    display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none",
  };
  const icon = (
    <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
  );
  if (isUrl) {
    return (
      <a href={clean} target="_blank" rel="noopener noreferrer" style={baseStyle}>
        {icon}{clean}
      </a>
    );
  }
  return (
    <button style={baseStyle}>
      {icon}{clean}
    </button>
  );
};

const CONTACTS = []; // Legacy demo fixture kept only as an empty sentinel for lint. Live data is fetched from /api/contacts inside the root component.

// Map backend contact record → UI shape expected by ContactCard/ContactPanel.
// Confidence in the backend is a string ("high"/"medium"/"low"/""); the UI
// expects a 0-100 number. Everything else is a straight field rename.
const CONFIDENCE_MAP = { high: 85, medium: 65, low: 40 };
const CANONICAL_ROLES = new Set(["Champion", "Economic Buyer", "Technical Buyer", "Procurement", "End User"]);
const normalizeRole = (r) => {
  const s = String(r || "").trim();
  if (!s) return "";
  // Try title-case for known variants; fall back to raw.
  const tc = s.replace(/\b\w/g, (c) => c.toUpperCase());
  return CANONICAL_ROLES.has(tc) ? tc : (CANONICAL_ROLES.has(s) ? s : tc);
};
const mapBackendContact = (c) => {
  const conf = typeof c.confidence === "number"
    ? c.confidence
    : (CONFIDENCE_MAP[String(c.confidence || "").toLowerCase()] || 55);
  return {
    id: c.id,
    name: c.name || "",
    title: c.title || "",
    role: normalizeRole(c.role_type),
    company: c.company || "",
    linkedin: c.linkedin_url || "",
    confidence: conf,
    identified: !!c.identified,
    notes: c.profile_context || "",
    signals: [],
    evidence: c.linkedin_url ? [c.linkedin_url] : [],
    aliases: [],
    mismatchFlag: false,
    lastUpdated: "",
    analysisId: c.analysis_id,
    source: c.source,
  };
};

/* ── Contact Detail Panel ── */
const ContactPanel = ({ contact, onClose }) => (
  <div
    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", backdropFilter: "blur(4px)" }}
    onClick={onClose}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        width: 520, height: "100vh", background: SURFACE, borderLeft: `1px solid ${BORDER}`,
        overflowY: "auto", animation: "slideIn 0.25s ease",
      }}
    >
      {/* Header */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{
          width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
          background: contact.identified ? `${roleColor(contact.role)}22` : SURFACE_2,
          border: `2px solid ${contact.identified ? roleColor(contact.role) + "66" : BORDER}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, fontWeight: 700, color: contact.identified ? roleColor(contact.role) : TEXT_DIM,
        }}>
          {contact.identified ? contact.name.split(" ").map(n => n[0]).join("").slice(0, 2) : "?"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{contact.name}</h2>
            {contact.mismatchFlag && <Badge label="MISMATCH FLAG" color="#EF4444" />}
          </div>
          <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 4 }}>{contact.title}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Badge label={contact.role.toUpperCase()} color={roleColor(contact.role)} />
            <Badge label={contact.identified ? "IDENTIFIED" : "NOT IDENTIFIED"} color={contact.identified ? "#22C55E" : TEXT_DIM} />
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_MUTED, padding: 4 }}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div style={{ padding: "20px 24px" }}>
        {/* Confidence */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>IDENTITY CONFIDENCE</div>
          {contact.identified ? <ConfBar value={contact.confidence} /> : (
            <div style={{ padding: "10px 14px", background: "#1C0A00", border: "1px solid #78350F", borderRadius: 7 }}>
              <span style={{ fontSize: 12, color: "#F59E0B" }}>Contact not yet identified — inference based on role pattern and org structure.</span>
            </div>
          )}
        </div>

        {/* Details */}
        {[
          { label: "COMPANY", value: contact.company },
          { label: "LOCATION", value: contact.location || "Unknown" },
          { label: "LINKEDIN", value: contact.linkedin, link: true },
          { label: "LAST UPDATED", value: contact.lastUpdated || "—" },
        ].map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", minWidth: 100, letterSpacing: "0.06em" }}>{r.label}</span>
            {r.link && r.value !== "—" && r.value
              ? <span style={{ fontSize: 12, color: ACCENT, cursor: "pointer" }}>{r.value}</span>
              : <span style={{ fontSize: 12, color: r.value === "Unknown" || r.value === "—" ? TEXT_DIM : TEXT_PRIMARY }}>{r.value}</span>
            }
          </div>
        ))}

        {/* Aliases */}
        {contact.aliases?.length > 0 && (
          <div style={{ padding: "12px 0", borderBottom: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.06em" }}>KNOWN ALIASES</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {contact.aliases.map(a => (
                <span key={a} style={{ fontSize: 11, color: TEXT_MUTED, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 8px" }}>{a}</span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div style={{ marginTop: 18, marginBottom: 18 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>ANALYST NOTES</div>
          <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.7 }}>{contact.notes}</p>
        </div>

        {/* Signals */}
        {contact.signals?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>SUPPORTING SIGNALS</div>
            {contact.signals.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 7 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: roleColor(contact.role), marginTop: 5, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>{s}</span>
              </div>
            ))}
          </div>
        )}

        {/* Evidence */}
        {contact.evidence?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 8 }}>EVIDENCE SOURCES</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {contact.evidence.map(e => <EvidenceChip key={e} label={e} />)}
            </div>
          </div>
        )}

        {/* Mismatch warning */}
        {contact.mismatchFlag && (
          <div style={{ background: "#200A0A", border: "1px solid #7F1D1D", borderRadius: 8, padding: "12px 14px", marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#EF4444", marginBottom: 4 }}>Identity Mismatch Flagged</div>
            <div style={{ fontSize: 12, color: "#FCA5A5" }}>Multiple candidate matches found. Confidence is low — verify before acting on this contact.</div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{
            flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
            padding: "10px", fontSize: 12, color: TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}>Add Note</button>
          <button style={{
            flex: 1, background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 8,
            padding: "10px", fontSize: 12, color: ACCENT, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
          }}>Confirm Identity</button>
        </div>
      </div>
    </div>
  </div>
);

/* ── Contact Card ── */
const ContactCard = ({ contact, onClick }) => {
  const rc = roleColor(contact.role);
  const cc = confColor(contact.confidence);

  return (
    <div
      onClick={() => onClick(contact)}
      style={{
        background: SURFACE, border: `1px solid ${contact.mismatchFlag ? "#7F1D1D" : BORDER}`,
        borderRadius: 10, padding: "16px 18px", cursor: "pointer",
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = contact.mismatchFlag ? "#EF4444" : "#2D3748"; e.currentTarget.style.background = SURFACE_2; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = contact.mismatchFlag ? "#7F1D1D" : BORDER; e.currentTarget.style.background = SURFACE; }}
    >
      {/* Avatar + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
          background: contact.identified ? `${rc}1A` : SURFACE_2,
          border: `2px solid ${contact.identified ? rc + "55" : BORDER}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700, color: contact.identified ? rc : TEXT_DIM,
        }}>
          {contact.identified ? contact.name.split(" ").map(n => n[0]).join("").slice(0, 2) : "?"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{contact.name}</span>
            {contact.mismatchFlag && (
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth={2}><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            )}
          </div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{contact.title}</div>
        </div>
      </div>

      {/* Role + status */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <Badge label={contact.role.toUpperCase()} color={rc} />
        <Badge label={contact.identified ? "IDENTIFIED" : "NOT IDENTIFIED"} color={contact.identified ? "#22C55E" : TEXT_DIM} />
      </div>

      {/* Confidence */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.05em" }}>CONFIDENCE</span>
          <span style={{ fontSize: 10, color: cc, fontFamily: "'DM Mono', monospace" }}>{contact.identified ? `${contact.confidence}%` : "—"}</span>
        </div>
        {contact.identified ? (
          <div style={{ height: 3, background: BORDER, borderRadius: 2 }}>
            <div style={{ width: `${contact.confidence}%`, height: "100%", background: cc, borderRadius: 2 }} />
          </div>
        ) : (
          <div style={{ height: 3, background: BORDER, borderRadius: 2 }} />
        )}
      </div>

      {/* Evidence chips */}
      {contact.evidence?.length > 0 && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
          {contact.evidence.slice(0, 2).map(e => <EvidenceChip key={e} label={e} />)}
          {contact.evidence.length > 2 && <span style={{ fontSize: 10, color: TEXT_DIM, alignSelf: "center" }}>+{contact.evidence.length - 2}</span>}
        </div>
      )}

      {contact.lastUpdated && (
        <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginTop: 10 }}>Updated {contact.lastUpdated}</div>
      )}
    </div>
  );
};

/* ── ROOT ── */
export default function InferenceContacts() {
  const [selected, setSelected] = useState(null);
  const [filterRole, setFilterRole] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterCompany, setFilterCompany] = useState("All");
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    contactsAPI.list()
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data?.contacts || [];
        setContacts(raw.map(mapBackendContact));
        setCompanies(["All", ...(res?.data?.companies || [])]);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load contacts");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const ROLES = ["All", "Champion", "Economic Buyer", "Technical Buyer", "Procurement", "End User"];
  const STATUSES = ["All", "Identified", "Not Identified"];

  const filtered = contacts.filter(c => {
    if (filterRole !== "All" && c.role !== filterRole) return false;
    if (filterStatus === "Identified" && !c.identified) return false;
    if (filterStatus === "Not Identified" && c.identified) return false;
    if (filterCompany !== "All" && c.company !== filterCompany) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${c.name} ${c.title} ${c.company} ${c.notes}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const identifiedCount = contacts.filter(c => c.identified).length;
  const gapCount = contacts.filter(c => !c.identified).length;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        button { outline: none; }
      `}</style>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>

        {/* Company selector */}
        <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
          {companies.map(c => (
            <button key={c} data-testid={`contacts-company-filter-${c}`} onClick={() => setFilterCompany(c)} style={{
              fontSize: 13, padding: "8px 18px", borderRadius: 8, cursor: "pointer",
              border: `1px solid ${filterCompany === c ? ACCENT : BORDER}`,
              background: filterCompany === c ? `${ACCENT}18` : SURFACE,
              color: filterCompany === c ? ACCENT : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif", fontWeight: filterCompany === c ? 600 : 400,
              transition: "all 0.15s",
            }}>{c}</button>
          ))}
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
          {[
            { label: "Total Contacts", value: contacts.length, color: TEXT_PRIMARY },
            { label: "Identified", value: identifiedCount, color: "#22C55E" },
            { label: "Gaps", value: gapCount, color: "#F59E0B" },
            { label: "Mismatch Flags", value: contacts.filter(c => c.mismatchFlag).length, color: "#EF4444" },
          ].map((s, i) => (
            <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.05em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* MEDDICC coverage strip */}
        <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px", marginBottom: 24 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 12 }}>DECISION-MAKER COVERAGE</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { role: "Champion", identified: true },
              { role: "Economic Buyer", identified: false },
              { role: "Technical Buyer", identified: true },
              { role: "Procurement", identified: false },
              { role: "End User", identified: false },
            ].map((r, i) => {
              const rc = roleColor(r.role);
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: r.identified ? `${rc}10` : SURFACE_2,
                  border: `1px solid ${r.identified ? rc + "44" : BORDER}`,
                  borderRadius: 8, padding: "8px 14px",
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: r.identified ? rc : TEXT_DIM }} />
                  <span style={{ fontSize: 12, color: r.identified ? TEXT_PRIMARY : TEXT_MUTED, fontWeight: r.identified ? 600 : 400 }}>{r.role}</span>
                  {!r.identified && (
                    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={2}><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <input
            data-testid="contacts-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search contacts by name, title, company…"
            style={{
              flex: "1 1 260px", minWidth: 200,
              background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 6,
              padding: "7px 12px", fontSize: 12, color: TEXT_PRIMARY,
              fontFamily: "'DM Sans', sans-serif", outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {ROLES.map(r => (
              <button key={r} data-testid={`contacts-role-filter-${r.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => setFilterRole(r)} style={{
                fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${filterRole === r ? (r === "All" ? ACCENT : roleColor(r) + "88") : BORDER}`,
                background: filterRole === r ? (r === "All" ? `${ACCENT}18` : `${roleColor(r)}14`) : "transparent",
                color: filterRole === r ? (r === "All" ? ACCENT : roleColor(r)) : TEXT_MUTED,
                fontFamily: "'DM Sans', sans-serif",
              }}>{r}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 20, background: BORDER }} />
          <div style={{ display: "flex", gap: 5 }}>
            {STATUSES.map(s => (
              <button key={s} data-testid={`contacts-status-filter-${s.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => setFilterStatus(s)} style={{
                fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${filterStatus === s ? ACCENT : BORDER}`,
                background: filterStatus === s ? `${ACCENT}18` : "transparent",
                color: filterStatus === s ? ACCENT : TEXT_MUTED,
                fontFamily: "'DM Sans', sans-serif",
              }}>{s}</button>
            ))}
          </div>
          <span style={{ marginLeft: "auto", fontSize: 12, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
            {filtered.length} contacts
          </span>
        </div>

        {/* Cards grid */}
        {loading ? (
          <div data-testid="contacts-loading" style={{ padding: "48px 0", textAlign: "center", color: TEXT_MUTED, fontSize: 13 }}>
            Loading contacts…
          </div>
        ) : error ? (
          <div data-testid="contacts-error" style={{ padding: "48px 0", textAlign: "center", color: "#EF4444", fontSize: 13 }}>
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="contacts-empty" style={{ padding: "60px 0", textAlign: "center" }}>
            <p style={{ fontSize: 14, color: TEXT_PRIMARY, marginBottom: 6 }}>
              {contacts.length === 0 ? "No contacts yet." : "No contacts match your filters."}
            </p>
            <p style={{ fontSize: 12, color: TEXT_MUTED }}>
              {contacts.length === 0
                ? "Run an analysis to discover decision-makers automatically."
                : "Try clearing filters or searching for a different term."}
            </p>
          </div>
        ) : (
          <div data-testid="contacts-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {filtered.map(c => (
              <ContactCard key={c.id} contact={c} onClick={setSelected} />
            ))}
          </div>
        )}
      </div>

      {selected && <ContactPanel contact={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
